import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	Id,
	isSuccess,
	useHasPermission,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { calendarEventService } from "../../../../shared/calendar/events/calendarEventService";
import { PermissionName } from "../../../../shared/types/permission";
import { eventService } from "../../shared/api/eventService";
import { EventDetailResource } from "../../shared/types";

interface Props {
	event: EventDetailResource;
}

export const EventContextMenu: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const { viewType = "" } = useParams<"viewType">();
	const canCreate = useHasPermission<PermissionName>(["event.create"]);
	const canUpdate = useHasPermission<PermissionName>(["event.update"]);
	const canDelete = useHasPermission<PermissionName>(["event.delete"]);

	const archiveEvent = async (eventId: Id) => {
		const response = await eventService.destroy(eventId);
		if (isSuccess(response)) {
			showToast({
				type: "success",
				title: t("Events"),
				body: t("The event was successfully archived."),
			});
			void queryClient.invalidateQueries({ queryKey: eventService.listOverviewKey });
			void queryClient.invalidateQueries({ queryKey: calendarEventService.calendarKey });
			navigate(`/events/${viewType}`, { replace: true });
		}
	};

	if (!canUpdate && !canDelete && !event.hasProjectMemberPermissions && !event.hasProjectManagerPermissions) {
		return null;
	}

	return (
		<ContextMenu hideIconOnOnlyShortcuts={true} displayShortcutsAs="icon">
			{(canUpdate || event.hasProjectMemberPermissions) && (
				<ContextMenuItem
					action={() => navigate(`/events/${viewType}/update/${event.id}/base-data`)}
					title={t("Edit event")}
					label={t("Edit event")}
					icon="edit"
					shortcut="primary"
				/>
			)}
			{canCreate && (
				<ContextMenuItem
					action={() => navigate(`/events/${viewType}/create/from-event/${event.id}/base-data`)}
					title={t("Duplicate event")}
					label={t("Duplicate event")}
					icon="copy"
				/>
			)}
			{(canUpdate || event.hasProjectManagerPermissions) && (
				<ContextMenuItem
					action={() => navigate(`/events/${viewType}/${event.id}/update-event-type/base-data`)}
					title={t("Change event type")}
					label={t("Change event type")}
					iconSrc="/change_event_type.svg"
				/>
			)}
			{(canDelete || event.hasProjectManagerPermissions) && (
				<ContextMenuDeleteItem
					action={() => archiveEvent(event.id)}
					title={t("Archive event")}
					label={t("Archive event")}
					heading={t("Archive event")}
					labels={{ close: t("Close"), confirm: t("Confirm") }}
				>
					{t("Are you sure you want to archive this event?")}
				</ContextMenuDeleteItem>
			)}
		</ContextMenu>
	);
};
